/**
 * Impressao — tela de PREVIEW da Lista de Compras dentro do app (fiel ao HTML).
 * Stage escuro + barra com "Voltar editar", "Baixar PDF", "Imprimir" e, no
 * fluxo ao vivo, "Salvar no histórico" (finalizar). As folhas A4 (DocumentoFolhas)
 * são as mesmas no preview, no PDF e na impressão.
 *
 * - `FolhasImpressao` (named): apresentacional, reusável (editor ao vivo + lista salva).
 * - default `Impressao`: rota do fluxo ao vivo (lê o contexto; finalizar = salvar).
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowLeft, FiDownload, FiPackage, FiPrinter, FiSave } from "react-icons/fi";
import styled from "styled-components";

import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { criarLista } from "../../services/revisorFerragens";
import { criarRequisicaoSeparacao } from "../../services/separacaoFerragens";
import { usePainelTerceiros } from "./PainelTerceiros";
import { enviarTerceirosDaLista } from "./enviarTerceiros";
import DocumentoFolhas from "./DocumentoFolhas";
import { descartarAmbientesPersistidos, useRevisor } from "./context";
import {
  ambientesLiveParaDocumento, montarPayloadLista, nomeArquivoPdf,
} from "./lib/documento";
import { baixarPdf, imprimirAgora } from "./lib/impressao";
import { EmptyBox, Page } from "./styles";

export function FolhasImpressao({
  ambientes, onVoltar, onFinalizar, onEnviarRequisicao,
  rotuloFinalizar = "Salvar no histórico",
}) {
  const toast = useToast();
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [finalizando, setFinalizando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  async function handlePdf() {
    setGerandoPdf(true);
    try {
      await baixarPdf(nomeArquivoPdf(ambientes));
      toast.success("PDF baixado.");
    } catch (err) {
      toast.error(err?.message || "Falha ao gerar o PDF.");
    } finally {
      setGerandoPdf(false);
    }
  }

  async function handleFinalizar() {
    if (!onFinalizar) return;
    setFinalizando(true);
    try {
      await onFinalizar();
    } catch (err) {
      toast.error(err?.message || "Falha ao salvar no histórico.");
      setFinalizando(false);
    }
    // sucesso navega para fora — não precisa resetar
  }

  async function handleEnviar() {
    if (!onEnviarRequisicao) return;
    setEnviando(true);
    try {
      const resultado = await onEnviarRequisicao();
      toast.success("Requisição enviada para o estoque.");
      // Anexo de terceiro sobe numa 2a request e NAO derruba o envio — mas o
      // usuario PRECISA saber, senao o comprador recebe o item sem o PDF e
      // ninguem fica sabendo (bug de producao 2026-08-06).
      const falhas = resultado?.anexosComFalha;
      if (falhas?.length) {
        toast.error(
          `O arquivo de ${falhas.join(", ")} não foi anexado. `
          + "Anexe pela tela de Requisições de Compra.",
        );
      }
    } catch (err) {
      toast.error(
        err?.payload?.detalhe || err?.message || "Falha ao enviar a requisição.",
      );
    } finally {
      // No fluxo ao vivo navega para fora (no-op benigno); na lista salva
      // permanece na tela, entao reabilita o botao.
      setEnviando(false);
    }
  }

  return (
    <Stage>
      <Toolbar>
        <ToolbarBtn type="button" onClick={onVoltar}>
          <FiArrowLeft size={14} /> Voltar editar
        </ToolbarBtn>
        <div style={{ flex: 1 }} />
        <ToolbarBtn type="button" onClick={handlePdf} disabled={gerandoPdf}>
          <FiDownload size={14} /> {gerandoPdf ? "Gerando…" : "Baixar PDF"}
        </ToolbarBtn>
        <ToolbarBtn type="button" onClick={imprimirAgora}>
          <FiPrinter size={14} /> Imprimir
        </ToolbarBtn>
        {onFinalizar ? (
          <ToolbarBtn
            type="button"
            $primary={!onEnviarRequisicao}
            onClick={handleFinalizar}
            disabled={finalizando}
          >
            <FiSave size={14} /> {finalizando ? "Salvando…" : rotuloFinalizar}
          </ToolbarBtn>
        ) : null}
        {onEnviarRequisicao ? (
          <ToolbarBtn type="button" $primary onClick={handleEnviar} disabled={enviando}>
            <FiPackage size={14} /> {enviando ? "Enviando…" : "Enviar requisição para o estoque"}
          </ToolbarBtn>
        ) : null}
      </Toolbar>

      <DocumentoFolhas ambientes={ambientes} />
    </Stage>
  );
}

export default function Impressao() {
  const { ambientes } = useRevisor();
  const { request } = useAuth();
  const { pedirTerceiros, painelTerceiros } = usePainelTerceiros();
  const navigate = useNavigate();

  const doc = useMemo(() => ambientesLiveParaDocumento(ambientes), [ambientes]);

  async function finalizar() {
    const lista = await criarLista(request, montarPayloadLista(ambientes));
    descartarAmbientesPersistidos();
    navigate(`../historico/${lista.id}`);
  }

  // Salva a lista (finaliza) E cria a requisicao de separacao para o estoque.
  // Antes disso pergunta pelos itens de TERCEIROS: e agora que o projetista
  // ainda tem o ambiente na cabeca. A Promise do painel segura o await do
  // botao, entao ele fica desabilitado enquanto a pessoa preenche.
  async function enviarRequisicao() {
    const terceiros = await pedirTerceiros();
    if (terceiros === null) return;  // desistiu: nao envia nada
    const lista = await criarLista(request, montarPayloadLista(ambientes));
    await criarRequisicaoSeparacao(request, lista.id);
    const resultado = await enviarTerceirosDaLista(request, lista.id, terceiros);
    descartarAmbientesPersistidos();
    navigate(`../historico/${lista.id}`);
    return resultado;  // handleEnviar avisa se algum anexo falhou
  }

  if (!ambientes.length) {
    return (
      <Page>
        <EmptyBox>
          Nenhum ambiente para imprimir.
          <div style={{ marginTop: 14 }}>
            <button className="btn btn--primary" onClick={() => navigate("..")}>
              Ir para projetos
            </button>
          </div>
        </EmptyBox>
      </Page>
    );
  }

  return (
    <>
      <FolhasImpressao
        ambientes={doc}
        onVoltar={() => navigate("../editor")}
        onFinalizar={finalizar}
        onEnviarRequisicao={enviarRequisicao}
      />
      {painelTerceiros}
    </>
  );
}

const Stage = styled.div`
  background: #0f0f14;
  margin: -20px -20px 0;
  padding: 18px 16px 60px;
  min-height: calc(100vh - 56px);
`;

const Toolbar = styled.div`
  max-width: 210mm;
  margin: 0 auto 22px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ToolbarBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  padding: 9px 16px;
  border-radius: 999px;
  cursor: pointer;
  border: 1px solid ${({ $primary }) => ($primary ? "var(--accent)" : "rgba(255,255,255,.18)")};
  background: ${({ $primary }) => ($primary ? "var(--accent)" : "rgba(255,255,255,.94)")};
  color: ${({ $primary }) => ($primary ? "var(--ink-on-accent, #fff)" : "#0f0f14")};

  &:hover:not(:disabled) { filter: brightness(1.05); }
  &:disabled { opacity: 0.6; cursor: default; }
`;
